/**
 * cc_splitSizeVariants.js
 * Used on trousers product pages (cc_product__form.liquid).
 *
 * Goals:
 * - Get a list of variants as a JSON object and check if they are in stock and available.
 * - Split variants into waist and leg variants and insert them into two dropdowns.
 * - On change of waist or leg dropdown, update variant-selection to the correct variant.
 * - Leg dropdown is disabled and not populated until waist dropdown is selected.
 * - Update button (e.g. add-to-cart) based on the selected variant.
 *
 * Usage / Data flow:
 * - Variants are read from <script type="application/json" data-product-variants-json> in the form
 *   (output by cc_product__form.liquid). Waist/leg are parsed from each variant's option1 (e.g. '28W 32L').
 * - We populate [data-cc-waist] and [data-cc-leg] selects, then sync the chosen waist+leg to the
 *   form's hidden select[data-variants] and update the add-to-cart button (data-add-to-cart-trigger).
 * - If the form has data-product-json-url, we fetch live availability from the product .js endpoint
 *   and merge it in so in-stock checks stay accurate despite Liquid caching.
 */

// ——— Get variants from DOM ———

/**
 * Gets the list of product variants from the form as a JSON object.
 *
 * Source: cc_product__form.liquid outputs all product.variants into a
 * <script type="application/json" data-product-variants-json> tag so this
 * script can read them without relying on the theme's variant-selection DOM.
 *
 * @returns {Array<{id: number, option1: string, available: boolean, title: string}>}
 *   Array of variant objects (only id, option1, available, title — no option2/option3). [] if missing or invalid.
 */
function getVariants() {
  const el = document.querySelector('[data-product-variants-json]');
  if (!el || !el.textContent || !el.textContent.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(el.textContent.trim());
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

// ——— Resolve / fetch product JSON (live availability) ———

/**
 * Resolves product .js URL to an absolute URL so fetch works from any page.
 * @param {string} productJsonUrl - e.g. "/products/foo.js" or full URL
 * @returns {string}
 */
function resolveProductJsonUrl(productJsonUrl) {
  if (!productJsonUrl || typeof productJsonUrl !== 'string') return '';
  const s = productJsonUrl.trim();
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return origin + (s.startsWith('/') ? s : '/' + s);
}

/**
 * Fetches live product data from Shopify's product JSON endpoint (e.g. /products/foo.js).
 * Use this for accurate, up-to-date availability instead of Liquid (which can be cached).
 *
 * @param {string} productJsonUrl - Full or relative URL to product JSON (data-product-json-url from form).
 * @returns {Promise<Array<{id: number, available: boolean, inventory_quantity?: number}>>} Variants with live availability, or [] on error.
 */
function fetchLiveVariants(productJsonUrl) {
  const url = resolveProductJsonUrl(productJsonUrl);
  if (!url) return Promise.resolve([]);
  return fetch(url)
    .then((res) => {
      if (!res.ok) return Promise.reject(new Error('Product fetch failed: ' + res.status));
      return res.json();
    })
    .then((product) => (product && product.variants ? product.variants : []))
    .catch(() => []);
}

/**
 * Merges live availability (from product .js) into variant objects by id.
 * Updates .available from live when present. Only overwrites .inventory_quantity when the
 * live response explicitly includes it (so we keep Liquid's quantity if the API omits it
 * or uses a different location).
 *
 * @param {Array<{id: number, available?: boolean, inventory_quantity?: number}>} variants - Mutable array to update
 * @param {Array<{id: number, available?: boolean, available_for_sale?: boolean, inventory_quantity?: number}>} liveVariants - Response from product .js
 */
function mergeLiveAvailability(variants, liveVariants) {
  if (!Array.isArray(liveVariants) || !liveVariants.length) return;
  const byId = new Map(liveVariants.map((v) => [v.id, v]));
  variants.forEach((v) => {
    const live = byId.get(v.id);
    if (live) {
      const available = live.available !== undefined ? !!live.available : !!live.available_for_sale;
      v.available = available;
      if (typeof live.inventory_quantity === 'number') v.inventory_quantity = live.inventory_quantity;
    }
  });
}

// ——— Waist/leg parsing and options ———

/**
 * Returns whether a variant is in stock / purchasable.
 * Uses variant.available when set; otherwise falls back to inventory_quantity + inventory_policy.
 * When policy is "deny", also treats as in stock if inventory_quantity > 0 (covers cases where
 * Shopify's available flag is false due to location but Liquid has correct quantity).
 *
 * @param {{ available?: boolean, inventory_quantity?: number, inventory_management?: string, inventory_policy?: string }} variant
 * @returns {boolean}
 */
function isInStock(variant) {
  if (!variant) return false;
  const policy = (variant.inventory_policy || 'deny').toLowerCase();
  const qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : 0;
  const managed = variant.inventory_management != null && variant.inventory_management !== '';

  if (managed && policy === 'deny' && qty > 0) return true;
  if (managed && policy === 'deny' && qty <= 0) return false;

  if (typeof variant.available === 'boolean') return variant.available;
  return true;
}

/**
 * Reads the resolved split format from the product form.
 * Set in Liquid from the Form block, product metafield, or auto-detect.
 * @returns {'waist_length'|'uk_length'}
 */
function getSplitSizeFormat() {
  const form = document.querySelector('[data-product-form]');
  const raw = form && form.dataset ? form.dataset.splitSizeFormat : '';
  const format = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (format === 'uk_length' || format === 'uk' || format === 'last_token') return 'uk_length';
  return 'waist_length';
}

/**
 * Parses option1 into a first and second size part.
 * waist_length: first token / rest ('28W 32L', '23W Regular', '34C regularL')
 * uk_length: all but last token / last token ('UK 8 Short', 'UK 10 Regular')
 * @param {string} option1
 * @param {'waist_length'|'uk_length'} [format]
 * @returns {{ waist: string|null, leg: string|null }}
 */
function parseWaistAndLeg(option1, format) {
  if (!option1 || typeof option1 !== 'string') return { waist: null, leg: null };
  const parts = option1.trim().split(/\s+/);
  if (parts.length < 2) return { waist: null, leg: null };
  if (format === 'uk_length') {
    return { waist: parts.slice(0, -1).join(' '), leg: parts[parts.length - 1] };
  }
  return { waist: parts[0], leg: parts.slice(1).join(' ') };
}

/**
 * Sort key for size tokens: UK 8 → 8, 32W → 32, Short → 0 then localeCompare.
 * @param {string} value
 * @returns {number}
 */
function sizeSortValue(value) {
  const uk = String(value).match(/UK\s*(\d+)/i);
  if (uk) return parseInt(uk[1], 10);
  const num = parseInt(value, 10);
  return Number.isNaN(num) ? 0 : num;
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function compareSizeOptions(a, b) {
  const diff = sizeSortValue(a) - sizeSortValue(b);
  if (diff !== 0) return diff;
  return String(a).localeCompare(String(b));
}

/**
 * Unique waist options (e.g. ['28W', '30W', '32W']) and leg options (e.g. ['32L', '34L'])
 * derived from all variants. Each variant's option1 is "Waist Leg" (e.g. '28W 32L').
 *
 * @param {Array<{option1: string}>} variants
 * @returns {{ waistOptions: string[], legOptions: string[] }}
 */
function getWaistAndLegOptions(variants, format) {
  const waistSet = new Set();
  const legSet = new Set();
  for (const v of variants) {
    const { waist, leg } = parseWaistAndLeg(v.option1, format);
    if (waist) waistSet.add(waist);
    if (leg) legSet.add(leg);
  }
  return {
    waistOptions: [...waistSet].sort(compareSizeOptions),
    legOptions: [...legSet].sort(compareSizeOptions)
  };
}

/**
 * Leg options that actually pair with a given waist (e.g. '28W' → ['30L', '32L']),
 * derived only from variants whose parsed waist matches — mirrors
 * findVariantByWaistAndLeg's matching logic, just one-sided (waist only).
 *
 * Used to determine which of the leg <select>'s options are real combinations for the
 * selected waist vs. ones that only exist for other waists (see getWaistAndLegOptions,
 * which still returns the full global leg list used to build the <option> elements
 * themselves — this narrower set drives which of those elements get disabled).
 *
 * @param {Array<{option1: string}>} variants
 * @param {string} waist - e.g. '30W'. Empty/falsy returns [].
 * @param {'waist_length'|'uk_length'} [format]
 * @returns {string[]}
 */
function getLegOptionsForWaist(variants, waist, format) {
  if (!waist) return [];
  const legSet = new Set();
  for (const v of variants) {
    const parsed = parseWaistAndLeg(v.option1, format);
    if (parsed.waist === waist && parsed.leg) legSet.add(parsed.leg);
  }
  return [...legSet].sort(compareSizeOptions);
}

/**
 * Find variant whose option1 parses to the given waist + leg (e.g. '30W 34L').
 * Compares parsed parts rather than rebuilding the string, so option1 values
 * with extra whitespace (e.g. '34W  Long') still match (#101).
 * @param {Array<{option1: string}>} variants
 * @param {string} waist - e.g. '30W'
 * @param {string} leg - e.g. '34L'
 * @param {'waist_length'|'uk_length'} [format]
 * @returns {{ option1: string, id: number, available?: boolean }|null}
 */
function findVariantByWaistAndLeg(variants, waist, leg, format) {
  if (!waist || !leg) return null;
  return (
    variants.find((v) => {
      const parsed = parseWaistAndLeg(v.option1, format);
      return parsed.waist === waist && parsed.leg === leg;
    }) || null
  );
}

// ——— Option display formatting ———

/**
 * Formats a raw variant size token for display in the dropdown.
 *
 * - Numeric + any letter suffix (e.g. '34W', '30L', '34C') → strips letters, appends inch mark: '34"'
 * - All-letter value (e.g. 'regular', 'long') → title-cases it: 'Regular', 'Long'
 * - Anything else is returned unchanged.
 *
 * The option's .value always stays as the original raw token so variant matching continues to work.
 *
 * @param {string} value - raw option token from a variant
 * @returns {string} formatted display label
 */
function formatSizeOption(value) {
  if (!value) return value;
  const numericMatch = value.match(/^(\d+)[A-Za-z]+$/);
  if (numericMatch) return numericMatch[1] + '"';
  if (/^[A-Za-z]+$/.test(value)) {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
  return value;
}

// ——— Populate dropdowns and sync to form ———

/**
 * Populates the waist and leg selects (cc_product__waist-leg-select.liquid),
 * disables leg until waist is chosen, and syncs selection to the form variant-selection.
 * Fetches live availability from product .js when data-product-json-url is present.
 */
function initWaistLegSelects() {
  const variants = getVariants();
  if (!variants.length) return;

  const waistSelect = document.querySelector('[data-cc-waist]');
  const legSelect = document.querySelector('[data-cc-leg]');
  if (!waistSelect || !legSelect) return;

  const formContainer = document.querySelector('[data-product-form]');
  const productJsonUrl = formContainer?.dataset?.productJsonUrl || null;

  /**
   * Runs after we have variants (and optionally after live availability merge).
   * Populates waist/leg options, wires change handlers, and syncs to variant select + add-to-cart.
   */
  function runWithVariants() {
    const splitFormat = getSplitSizeFormat();
    const { waistOptions, legOptions } = getWaistAndLegOptions(variants, splitFormat);

    for (const w of waistOptions) {
      const opt = document.createElement('option');
      opt.value = w;
      opt.textContent = formatSizeOption(w);
      waistSelect.appendChild(opt);
    }

    for (const l of legOptions) {
      const opt = document.createElement('option');
      opt.value = l;
      opt.textContent = formatSizeOption(l);
      legSelect.appendChild(opt);
    }

    const form = waistSelect.closest('form') || waistSelect.closest('[data-product-form]');
    const variantSelect = form ? form.querySelector('select[data-variants]') : null;
    const waistDisplay = document.querySelector('[data-cc-waist-display]');
    const legDisplay = document.querySelector('[data-cc-leg-display]');

    const productSection = form ? form.closest('section') : null;
    const addToCartBtn =
      (productSection ? productSection.querySelector('[data-add-to-cart-trigger]') : null) ||
      (form ? form.querySelector('[data-add-to-cart-trigger]') : null);
    const addToCartLabel =
      addToCartBtn && addToCartBtn.getAttribute('data-label')
        ? addToCartBtn.getAttribute('data-label')
        : 'Add to cart';
    const outOfStockText =
      typeof window !== 'undefined' &&
      window.PXUTheme &&
      window.PXUTheme.translation &&
      window.PXUTheme.translation.out_of_stock
        ? window.PXUTheme.translation.out_of_stock
        : 'Out of stock';
    const unavailableText =
      typeof window !== 'undefined' &&
      window.PXUTheme &&
      window.PXUTheme.translation &&
      window.PXUTheme.translation.unavailable
        ? window.PXUTheme.translation.unavailable
        : 'Select Your Size';
    const notifyText =
      typeof window !== 'undefined' &&
      window.PXUTheme &&
      window.PXUTheme.translation &&
      window.PXUTheme.translation.notify
        ? window.PXUTheme.translation.notify
        : 'Notify';
    const quantityEl = productSection ? productSection.querySelector('.purchase-details__quantity') : null;
    const shopPayEl = productSection ? productSection.querySelector('.shopify-payment-button') : null;

    function setButtonLabel(btn, text) {
      if (!btn) return;
      btn.setAttribute('data-label', text);
      if (btn.hasAttribute('data-original-text')) btn.setAttribute('data-original-text', text);
      const spans = btn.querySelectorAll('span:not(.icon):not([class*="icon"])');
      for (let i = 0; i < spans.length; i++) spans[i].textContent = text;
    }

    function updateDisplay(select, displayEl) {
      if (!displayEl) return;
      const opt = select.options[select.selectedIndex];
      const placeholder = select.options[0] ? select.options[0].textContent : '';
      displayEl.textContent = opt && opt.value ? opt.textContent : placeholder;
    }

    /** Update add-to-cart button, quantity box, notify form and Shop Pay based on selected variant and stock. */
    function updateAddToCartButton(selectedVariant) {
      if (!addToCartBtn) return;
      const notifyForm = productSection ? productSection.querySelector('.product__notify-form') : null;

      if (!selectedVariant) {
        // aria-disabled (not the native disabled attribute) so the button
        // still fires click events - needed to show the "select a size"
        // message when tapped. Label stays "Add to basket" throughout;
        // it no longer relabels itself to an instruction.
        addToCartBtn.setAttribute('aria-disabled', 'true');
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.classList.remove('disabled');
        setButtonLabel(addToCartBtn, addToCartLabel);
        if (notifyForm) notifyForm.style.display = 'none';
        if (quantityEl) quantityEl.style.display = 'none';
        if (shopPayEl) shopPayEl.style.display = 'none';
        return;
      }

      const inStock = isInStock(selectedVariant);
      if (inStock) {
        addToCartBtn.removeAttribute('aria-disabled');
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.classList.remove('disabled');
        setButtonLabel(addToCartBtn, addToCartLabel);
        if (notifyForm) notifyForm.style.display = 'none';
        if (quantityEl) quantityEl.style.display = '';
        if (shopPayEl) shopPayEl.style.display = '';
      } else {
        // Genuinely sold out (not just unselected) - this case is
        // unrelated to the "no size chosen yet" issue, so it keeps its
        // existing behaviour: real disabled attribute, relabelled button,
        // notify-me form revealed instead of an advisory message.
        addToCartBtn.setAttribute('disabled', 'disabled');
        addToCartBtn.removeAttribute('aria-disabled');
        addToCartBtn.classList.add('disabled');
        setButtonLabel(addToCartBtn, outOfStockText);
        if (notifyForm) notifyForm.style.display = '';
        if (quantityEl) quantityEl.style.display = 'none';
        if (shopPayEl) shopPayEl.style.display = 'none';
      }
    }

    /**
     * Re-evaluates the leg select's <option> elements against the currently selected waist:
     * - Options with no real waist+leg variant are disabled (not removed - keeps the full
     *   size range visible, matching the standard variant select's disabled-option convention).
     * - Options that resolve to a real but out-of-stock variant are relabelled with a
     *   "- Notify" suffix, kept selectable (picking one already routes into the existing
     *   sold-out add-to-cart path below).
     * - Valid, in-stock options get their plain formatted label.
     * If the currently-selected leg is no longer valid for the new waist, it's reset.
     * Called on waist change and before every sync (recheckAndSync), so labels stay
     * current after a live availability refetch.
     */
    function refreshLegOptions() {
      const waist = waistSelect.value;
      const validLegs = new Set(getLegOptionsForWaist(variants, waist, splitFormat));
      let selectedNowInvalid = false;

      for (const opt of legSelect.options) {
        if (!opt.value) continue; // skip the placeholder option
        const isValid = validLegs.has(opt.value);
        opt.disabled = !isValid;

        if (isValid) {
          const variant = findVariantByWaistAndLeg(variants, waist, opt.value, splitFormat);
          const outOfStock = variant && !isInStock(variant);
          opt.textContent = outOfStock
            ? formatSizeOption(opt.value) + ' – ' + notifyText
            : formatSizeOption(opt.value);
        } else {
          opt.textContent = formatSizeOption(opt.value);
        }

        if (opt.selected && !isValid) selectedNowInvalid = true;
      }

      if (selectedNowInvalid) legSelect.value = '';
      // The visible display span (cc_product__waist-leg-select.liquid's data-cc-leg-display)
      // mirrors the selected option's label and isn't otherwise refreshed on a waist change -
      // without this it can keep showing a stale "- Notify" (or lack of one) from before the
      // waist switched, even though the underlying <select> option text is already correct.
      updateDisplay(legSelect, legDisplay);
    }

    function syncVariantSelection() {
      const waist = waistSelect.value;
      const leg = legSelect.value;
      if (!waist) {
        legSelect.disabled = true;
        if (legSelect.value) legSelect.value = '';
        updateDisplay(legSelect, legDisplay);
        if (variantSelect) variantSelect.value = 'not-selected';
        setTimeout(function () {
          updateAddToCartButton(null);
        }, 0);
        return;
      }
      legSelect.disabled = false;
      if (!leg) {
        if (variantSelect) variantSelect.value = 'not-selected';
        setTimeout(function () {
          updateAddToCartButton(null);
        }, 0);
        return;
      }
      const variant = findVariantByWaistAndLeg(variants, waist, leg, splitFormat);
      if (variant && variantSelect) {
        const variantId = String(variant.id);
        const option = variantSelect.querySelector('option[value="' + variantId + '"]');
        if (option && option.hasAttribute('disabled')) {
          option.removeAttribute('disabled');
        }
        variantSelect.value = variantId;
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        setTimeout(function () {
          updateAddToCartButton(variant);
        }, 0);
        if (!isInStock(variant)) {
          [100, 300, 600].forEach(function (ms) {
            setTimeout(function () {
              updateAddToCartButton(variant);
            }, ms);
          });
        }
      } else {
        setTimeout(function () {
          updateAddToCartButton(null);
        }, 0);
      }
    }

    /** Re-fetch live availability then sync variant selection (so add-to-cart / back-in-stock state is current). */
    function recheckAndSync() {
      if (productJsonUrl) {
        fetchLiveVariants(productJsonUrl).then((live) => {
          if (live.length) mergeLiveAvailability(variants, live);
          refreshLegOptions();
          syncVariantSelection();
        });
      } else {
        refreshLegOptions();
        syncVariantSelection();
      }
    }

    waistSelect.addEventListener('change', () => {
      updateDisplay(waistSelect, waistDisplay);
      recheckAndSync();
    });
    legSelect.addEventListener('change', () => {
      updateDisplay(legSelect, legDisplay);
      recheckAndSync();
    });

    const variantSelectionEl = form ? form.querySelector('[data-variant-selection]') : null;
    if (variantSelectionEl) {
      variantSelectionEl.addEventListener('variant-change', function () {
        const waist = waistSelect.value;
        const leg = legSelect.value;
        if (waist && leg) {
          const v = findVariantByWaistAndLeg(variants, waist, leg, splitFormat);
          if (v && !isInStock(v)) {
            [0, 50, 150, 400].forEach(function (ms) {
              setTimeout(function () {
                updateAddToCartButton(v);
              }, ms);
            });
          }
        }
      });
    }

    refreshLegOptions();
    syncVariantSelection();
    updateDisplay(waistSelect, waistDisplay);
    updateDisplay(legSelect, legDisplay);
  }

  if (productJsonUrl) {
    fetchLiveVariants(productJsonUrl).then((live) => {
      if (live.length) mergeLiveAvailability(variants, live);
      runWithVariants();
    });
  } else {
    runWithVariants();
  }
}

// ——— Init and DOMContentLoaded ———

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWaistLegSelects);
} else {
  initWaistLegSelects();
}
